<script setup lang="ts">
import { reactive, ref } from 'vue'
import { useSubjectsStore } from '@/stores/subjects'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosButton from '@/components/ui/FosButton.vue'

const props = defineProps<{
  incidentId: string
  visible: boolean
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
  created: [subjectId: string]
}>()

const subjectsStore = useSubjectsStore()

const form = reactive({ first_name: '', last_name: '', risk_category: '' })
const saving = ref(false)
const errors = reactive({ first_name: false, last_name: false })

function close() {
  emit('update:visible', false)
}

async function confirm() {
  errors.first_name = !form.first_name.trim()
  errors.last_name = !form.last_name.trim()
  if (errors.first_name || errors.last_name) return

  saving.value = true
  try {
    const id = await subjectsStore.createSubject(props.incidentId, {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      risk_category: form.risk_category.trim() || null,
    })
    form.first_name = ''
    form.last_name = ''
    form.risk_category = ''
    emit('created', id)
    close()
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <FosDialog :open="visible" title="Add Subject" @update:open="emit('update:visible', $event)">
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1.5">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest">
          First name <span class="text-error">*</span>
        </label>
        <FosInput
          v-model="form.first_name"
          placeholder="First name"
          @keydown.enter="confirm"
        />
        <span v-if="errors.first_name" class="text-[10px] text-error font-mono">Required</span>
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest">
          Last name <span class="text-error">*</span>
        </label>
        <FosInput
          v-model="form.last_name"
          placeholder="Last name"
          @keydown.enter="confirm"
        />
        <span v-if="errors.last_name" class="text-[10px] text-error font-mono">Required</span>
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest">Risk category</label>
        <FosInput
          v-model="form.risk_category"
          placeholder="e.g. Dementia, Juvenile"
          @keydown.enter="confirm"
        />
      </div>
    </div>

    <template #footer>
      <FosButton variant="tertiary" @click="close">Cancel</FosButton>
      <FosButton variant="primary" :disabled="saving" @click="confirm">
        <span v-if="saving" class="w-3.5 h-3.5 border-2 border-white/30 border-t-white animate-spin" />
        Add Subject
      </FosButton>
    </template>
  </FosDialog>
</template>
